import { PATH_SEPARATOR } from './const'
import { PathSegment } from './identifiable'
import { type Serializable } from './serializable'
import { ChangeAction, type ChangeEventSource, type Hierarchical, type HierarchyObjectCollection, type Identifiable, type Pathed, type onChangeCallback } from './types'

export function isHierarchyObject (object: unknown): object is HierarchyObject {
  return Object.prototype.hasOwnProperty.call(object, 'isHierarchyObject')
}

export type resolverFunction = (src: any) => Hierarchical

export class HierarchyObject extends PathSegment implements Hierarchical, Identifiable, Serializable, Pathed, ChangeEventSource<Hierarchical> {
  parent: Hierarchical | null = null
  children: HierarchyObjectCollection
  readonly isHierarchyObject: boolean = true
  protected onChangeSubscriptions: Set<onChangeCallback<Hierarchical>> | undefined
  protected readonly boundOnChildrenChanged: onChangeCallback<Hierarchical>

  constructor (id: string) {
    super(id)
    this.children = new HierarchyObjectMap()
    this.boundOnChildrenChanged = this._onChildrenChanged.bind(this) as onChangeCallback<Hierarchical>
    this.children.addChangeListener(this.boundOnChildrenChanged)
  }

  public onChildrenChanged (item: Hierarchical, action: ChangeAction): void {
  }

  public _onChildrenChanged (item: Hierarchical, action: ChangeAction): void {
    // console.debug('onChildrenChanged', this.path, this.onChangeSubscriptions)
    this.onChildrenChanged(item, action)
    if (this.onChangeSubscriptions) {
      this.onChangeSubscriptions.forEach(subscription => {
        subscription(item, action)
      })
    }
  }

  public addChangeListener (notify: onChangeCallback<Hierarchical>): void {
    if (!this.onChangeSubscriptions) {
      this.onChangeSubscriptions = new Set<onChangeCallback<Hierarchical>>()
    }
    this.onChangeSubscriptions.add(notify)
  }

  public removeChangeListener (notify: onChangeCallback<Hierarchical>): boolean {
    if (!this.onChangeSubscriptions) {
      return false
    }
    return this.onChangeSubscriptions.delete(notify)
  }

  static resolver: resolverFunction = (src: any) => {
    throw new Error('No resolver specified')
  }

  public read (o: any): this {
    this.children.clear()
    if (Object.prototype.hasOwnProperty.call(o, 'id')) {
      this._id = o.id
    } else {
      throw new Error('id is missing')
    }
    if (Object.prototype.hasOwnProperty.call(o, 'children')) {
      this.children.read(o.children, this)
      this.children.forEach((value: Hierarchical, key: string) => {
        value.parent = this
      })
    }
    return this
  }

  public save (): any {
    const result = {
      id: this.id
    } as any
    if (this.children.size > 0) {
      result.children = this.children.save()
    }
    return result
  }

  public addChild (item: Hierarchical): void {
    if (item.parent != null) {
      throw new Error("item '" + item.id + "' has alerady a parent")
    }
    this.children.set(item.id, item)
    item.parent = this
  }

  public containsChild (child: Hierarchical | string): boolean {
    let id: string
    if (typeof (child) === 'string') {
      id = child
    } else {
      id = child.id
    }
    return this.children.has(id)
  }

  public inPath (path: string): boolean {
    return ((this.path === path) || (path.startsWith(this.path + PATH_SEPARATOR)))
  }

  public removeChild (child: Hierarchical | string): void {
    let id: string
    if (typeof (child) === 'string') {
      id = child
    } else {
      if (child.parent == null) {
        throw new Error('not a child')
      }
      if (child.parent !== this) {
        throw new Error('child of another item')
      }
      id = child.id
    }

    if (!this.children.has(id)) {
      throw new Error('not a child of this')
    }

    if (typeof (child) === 'string') {
      const oldChild = this.children.get(id)
      if (oldChild != null) {
        oldChild.parent = null
      }
    } else {
      child.parent = null
    }
    this.children.delete(id)
  }

  get path (): string {
    let p: Hierarchical | null = this.parent
    let path = this.id
    while (p != null) {
      path = p.id + PATH_SEPARATOR + path
      p = p.parent
    }
    return path
  }
}

export function isHierarchyObjectCollection (object: unknown): object is HierarchyObjectCollection {
  return Object.prototype.hasOwnProperty.call(object, 'isHierarchyCollection')
}

export class HierarchyObjectMap implements HierarchyObjectCollection {
  private readonly items: Map<string, Hierarchical>
  readonly isHierarchyCollection: boolean = true
  private onChangeSubscriptions: Set<onChangeCallback<Hierarchical>> | undefined
  private readonly boundOnItemChanged: onChangeCallback<Hierarchical>
  t!: new () => Hierarchical

  constructor () {
    this.items = new Map<string, Hierarchical>()
    this.boundOnItemChanged = this.onItemChanged.bind(this)
  }

  public addChangeListener (notify: onChangeCallback<Hierarchical>): void {
    if (!this.onChangeSubscriptions) {
      this.onChangeSubscriptions = new Set<onChangeCallback<Hierarchical>>()
    }
    this.onChangeSubscriptions.add(notify)
  }

  public removeChangeListener (notify: onChangeCallback<Hierarchical>): boolean {
    if (!this.onChangeSubscriptions) {
      return false
    }
    return this.onChangeSubscriptions.delete(notify)
  }

  public onItemChanged (item: Hierarchical, action: ChangeAction): void {
    // console.debug('onChildrenChanged', this.onItemChanged)
    if (this.onChangeSubscriptions) {
      this.onChangeSubscriptions.forEach(subscription => {
        subscription(item, action)
      })
    }
  }

  read (o: any, parent?: any): this {
    this.items.clear()
    if (this.onChangeSubscriptions) {
      this.onChangeSubscriptions.clear()
    }
    if (typeof (o) === 'string') {
      o = JSON.parse(o)
    }
    if (!Array.isArray(o)) {
      if (Object.keys(o).length === 0) {
        return this
      }
      throw new Error('invalid data: ' + JSON.stringify(o))
    }
    o.forEach((src: any) => {
      const item = HierarchyObject.resolver(src)
      item.parent = parent
      const unk = item as unknown
      this.set(item.id, unk as Hierarchical)
    })
    return this
  }

  save (): any {
    const result = new Array<Hierarchical>()
    this.items.forEach((item: Hierarchical) => {
      result.push(item.save())
    })
    return result
  }

  static load (o: any): HierarchyObjectCollection {
    if (typeof o === 'string') {
      o = JSON.parse(o)
    }
    const c = new HierarchyObjectMap()
    c.read(o)
    return c
  }

  clear (): void {
    this.items.forEach((item, key) => {
      if (isHierarchyObject(item)) {
        item.removeChangeListener(this.boundOnItemChanged)
      }
    })
    this.items.clear()
  }

  delete (key: string): boolean {
    const parts = key.split(PATH_SEPARATOR)
    if (parts.length > 1) {
      const item = this.get(parts.slice(0, parts.length - 1).join(PATH_SEPARATOR))
      if (!item) {
        return false
      }
      const keyToDelete = parts[parts.length - 1]
      if (this.onChangeSubscriptions) {
        const itemToDelete = item.children.get(keyToDelete)
        if (itemToDelete) {
          this.onChangeSubscriptions.forEach(subscription => {
            subscription(itemToDelete, ChangeAction.Delete)
          })
        }
      }
      return (item.children.delete(keyToDelete))
    }
    if (!this.items.has(key)) {
      return false
    }
    const item = this.items.get(key)
    if (isHierarchyObject(item)) {
      item.removeChangeListener(this.boundOnItemChanged)
    }

    return this.items.delete(key)
  }

  getFlatMap (target?: Map<string, Hierarchical>): Map<string, Hierarchical> {
    if (target == null) {
      target = new Map<string, Hierarchical>()
    }
    this.items.forEach((value: Hierarchical, key: string, map: Map<string, Hierarchical>) => {
      if (isHierarchyObjectCollection(value)) {
        value.children.getFlatMap(target)
      } else {
        const item = value
        if (target != null) {
          target.set(item.path, item)
        }
        item.children.getFlatMap(target)
      }
    })
    return target
  }

  forAny (notifyfn: (value: Hierarchical, key: string, map: Map<string, Hierarchical>) => void, thisArg?: any): void {
    const map = this.getFlatMap()
    map.forEach(notifyfn, thisArg)
  }

  forEach (notifyfn: (value: Hierarchical, key: string, map: Map<string, Hierarchical>) => void, thisArg?: any): void {
    this.items.forEach(notifyfn, thisArg)
  }

  get (key: string): Hierarchical | undefined {
    let path = ''
    const i = key.indexOf(PATH_SEPARATOR)
    if (i > -1) {
      path = key.substring(i + 1)
      key = key.substring(0, i)
    }
    const r = this.items.get(key)
    if ((r != null) && path !== '') {
      const v = r.children.get(path)
      if (v != null) {
        return v
      }
      return undefined
    }
    return r
  }

  add (key: string, item: Hierarchical, parent?: Hierarchical): void {
    let path = ''
    const i = key.indexOf(PATH_SEPARATOR)
    if (i > -1) {
      path = key.substring(i + 1)
      key = key.substring(0, i)
    }
    if (path !== '') {
      let r: Hierarchical | undefined
      if (!this.items.has(key)) {
        r = new HierarchyObject(key)
        this.items.set(key, r)
        if (parent) {
          r.parent = parent
        }
      }
      r = this.items.get(key)
      if ((r != null) && path !== '') {
        r.children.add(path, item, r)
      }
      return
    }
    if (parent) {
      item.parent = parent
    }
    const action = this.items.has(key) ? ChangeAction.Change : ChangeAction.Add
    this.items.set(key, item)
    if (this.onChangeSubscriptions) {
      this.onChangeSubscriptions.forEach(subscription => {
        subscription(item, action)
      })
    }
  }

  has (key: string): boolean {
    let path = ''
    const i = key.indexOf(PATH_SEPARATOR)
    if (i > -1) {
      path = key.substring(i + 1)
      key = key.substring(0, i)
    }
    const r = this.items.get(key)
    if ((r != null) && path !== '') {
      return r.children.has(key)
    }
    return this.items.has(key)
  }

  set (key: string, value: Hierarchical): this {
    if (key.includes(PATH_SEPARATOR)) {
      throw new Error('set can not be used with pathes ("' + key + '")')
    }

    if (isHierarchyObject(value)) {
      value.addChangeListener(this.boundOnItemChanged)
    }

    const action = this.items.has(key) ? ChangeAction.Change : ChangeAction.Add
    this.items.set(key, value)
    // console.debug('set', key, this.onChangeSubscriptions)
    if (this.onChangeSubscriptions) {
      this.onChangeSubscriptions.forEach(subscription => {
        subscription(value, action)
      })
    }
    return this
  }

  get size (): number {
    return this.items.size
  }

  get deepSize (): number {
    return this.getFlatMap().size
  }

  entries (): IterableIterator<[string, Hierarchical]> {
    return this.items.entries()
  }

  keys (): IterableIterator<string> {
    return this.items.keys()
  }

  pathes (): IterableIterator<string> {
    return this.getFlatMap().keys()
  }

  values (): IterableIterator<Hierarchical> {
    return this.items.values()
  }

  [Symbol.iterator] (): IterableIterator<[string, Hierarchical]> {
    return this.items[Symbol.iterator]()
  }

  [Symbol.toStringTag]: string = 'ItemCollextion'
}
